import { get } from './_core.js';

export interface SessionListItem {
  id: string;
  userId: string;
  userEmail: string;
  moduleId: string | null;
  startedAt: string;
  endedAt: string | null;
  totalDurationMs: number | null;
  totalCostNano: string | null;
  /**
   * Suma de raw provider cost (sin markup) por sesión. El BO usa este por
   * default en vistas operativas (Fase 34). Opcional para tests viejos —
   * lectores usan `?? totalCostNano` con fallback.
   */
  totalCostRawNano?: string | null;
  turnCount: number;
  errorCount: number;
}

export interface SessionListFilters {
  from?: string;
  to?: string;
  userId?: string;
  moduleId?: string;
  hasError?: boolean;
  minCostNano?: string;
  limit?: number;
  cursor?: string;
  /** Fase 39 — default false (oculta sintéticas). */
  includeSynthetic?: boolean;
  /**
   * Fase 42 — `true` restringe a sesiones en curso (ended_at IS NULL).
   * Default false (lista incluye live + finalizadas). El BO usa esto en
   * `/sessions?live=1` y en el auto-refresh agresivo.
   */
  onlyLive?: boolean;
}

export interface SessionListResponse {
  items: SessionListItem[];
  nextCursor: string | undefined;
  totalApprox: number | undefined;
}

export async function fetchSessions(filters: SessionListFilters = {}): Promise<SessionListResponse> {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.userId) params.set('user_id', filters.userId);
  if (filters.moduleId) params.set('module_id', filters.moduleId);
  if (filters.hasError) params.set('has_error', 'true');
  if (filters.minCostNano) params.set('min_cost_nano', filters.minCostNano);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.includeSynthetic) params.set('include_synthetic', 'true');
  if (filters.onlyLive) params.set('live', 'true');
  const qs = params.toString();
  const res = await get<SessionListItem[]>(`/admin/sessions${qs ? `?${qs}` : ''}`);
  return { items: res.data, nextCursor: res.meta?.nextCursor, totalApprox: res.meta?.totalApprox };
}

export interface TimelineSpan {
  id: string;
  parentId: string | null;
  kind: string;
  name: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  costNano: string | null;
  /**
   * Raw provider cost (sin markup) — preferido por las vistas BO operativas
   * (Fase 34). `null` o `undefined` en spans pre-migration 0061 que no
   * fueron backfilleados (raros). Opcional para que tests/fixtures viejos
   * que no setean el campo sigan compilando — los lectores usan
   * `preferRawCost(s.providerCostNano, s.costNano)` con fallback.
   */
  providerCostNano?: string | null;
  /** Markup bps efectivo aplicado al span. `undefined` en spans pre-0061. */
  markupBps?: number | null;
  isError: boolean;
  errorKind: string | null;
  /**
   * Payload serializado del error (`{ name, message, stack }`). `null`/ausente
   * cuando el span no es error, en sesiones de módulos sensibles (gated), o en
   * fixtures viejos. El panel de detalle lo prefiere sobre `attributes` para el
   * mensaje real del error.
   */
  errorPayload?: unknown;
  attributes: Record<string, unknown>;
  sampleReason: string | null;
  callKind: string | null;
  callRefId: string | null;
  hasIo: boolean;
}

export interface SpanHole {
  parentSpanId: string;
  approxStartedAt: string;
  approxDurationMs: number;
  reason: string;
}

export interface TimelineSession {
  id: string;
  userId: string;
  userEmail: string;
  moduleId: string | null;
  startedAt: string;
  endedAt: string | null;
  totalDurationMs: number;
  totalCostNano: string;
  /** Raw provider cost total (sin markup) — preferido en vistas BO. */
  totalCostRawNano?: string;
  turnCount: number;
  errorCount: number;
  /**
   * Agregados de tokens LLM (denormalizados en voice_sessions). `tokensIn` es
   * input NO cacheado; `cacheReadTokens` el leído del prompt cache. El header
   * deriva un hit-rate para mostrar que el costo ya contempla el cache.
   * Opcionales: sesiones viejas (pre-feature) o sin LLM pueden no traerlos.
   */
  tokensIn?: number;
  tokensOut?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  /** Hilo: sesión origen (resume) y continuaciones, para navegar entre ellas. */
  parentSessionId?: string | null;
  childSessionIds?: string[];
}

export interface TimelineResponse {
  session: TimelineSession;
  spans: TimelineSpan[];
  holes: SpanHole[];
  truncated: boolean;
}

export async function fetchTimeline(sessionId: string): Promise<TimelineResponse> {
  const res = await get<TimelineResponse>(`/admin/sessions/${encodeURIComponent(sessionId)}/timeline`);
  return res.data;
}

export interface TranscriptCorrection {
  userUtterance: string;
  correctedForm: string;
  explanation: string | null;
  category: string | null;
  severity: string;
}

export interface TranscriptTurn {
  id: string;
  turnIndex: number;
  userText: string | null;
  assistantText: string | null;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  timeToFirstAudioMs: number | null;
  interrupted: boolean;
  /**
   * Correcciones del módulo languages emitidas durante este turn (chips
   * que el coach reply NO menciona — el side-runner las extrae en paralelo).
   * Vacío en módulos no-languages o turnos sin errores detectables.
   */
  corrections: TranscriptCorrection[];
}

export interface TranscriptSession {
  id: string;
  userId: string;
  userEmail: string;
  moduleId: string | null;
  startedAt: string;
  endedAt: string | null;
  /** Hilo: sesión origen (resume) y continuaciones. Para el toggle "ver todo el hilo". */
  parentSessionId?: string | null;
  childSessionIds?: string[];
}

export interface TranscriptResponse {
  session: TranscriptSession;
  turns: TranscriptTurn[];
}

export async function fetchTranscript(sessionId: string): Promise<TranscriptResponse> {
  const res = await get<TranscriptResponse>(
    `/admin/sessions/${encodeURIComponent(sessionId)}/transcript`,
  );
  return res.data;
}

export interface AudioManifestItem {
  turnIndex: number;
  kind: 'user' | 'assistant';
  contentType: string;
  durationMs: number | null;
  sampleRate: number | null;
  bytes: number;
}

export interface AudioManifestResponse {
  sessionId: string;
  items: AudioManifestItem[];
}

export async function fetchAudioManifest(sessionId: string): Promise<AudioManifestResponse> {
  const res = await get<AudioManifestResponse>(
    `/admin/sessions/${encodeURIComponent(sessionId)}/audio-manifest`,
  );
  return res.data;
}

export function turnAudioUrl(
  sessionId: string,
  turnIndex: number,
  kind: 'user' | 'assistant',
): string {
  return `/api/admin/sessions/${encodeURIComponent(
    sessionId,
  )}/turns/${turnIndex}/audio?kind=${kind}`;
}

/** Dump completo de la sesión para copiar al portapapeles. Tipo laxo a
 *  propósito: el endpoint backend define la shape canónica y el caller
 *  serializa a JSON tal cual. */
export async function fetchSessionFull(
  sessionId: string,
  options: { includePrompts?: boolean } = {},
): Promise<unknown> {
  const qs = options.includePrompts ? '?include=prompts' : '';
  const res = await get<unknown>(
    `/admin/sessions/${encodeURIComponent(sessionId)}/full${qs}`,
  );
  return res.data;
}
